<script setup>
import { ref, onMounted } from 'vue'
import { generateToken } from '../utils/auth'
import { fetchUrlSources } from '../utils/common'

const emit = defineEmits(['next-step'])

// 状态变量
const models = ref([])
const selectedModel = ref('')
const controlNetTypes = ref([])
const selectedCNs = ref([])
const prompt = ref('')
const imageUrl = ref('')
const imagePreview = ref('')
const generatedImages = ref([])
const loading = ref(false)
const taskStatus = ref('')
const token = ref('')
const referenceUrls = ref({})
const sourcePrefix = ref('素材模板/图片')
const sourceUrls = ref([])
const cnSourcePrefix = ref('素材模板/图片')
const cnSourceUrls = ref([])
const loraModel = ref('')
const loraOptions = [
  {
    model_name: 'beauty_v20241223_4',
    model_id: 'ctkh5dgf27frci6d2840'
  },
  {
    model_name: 'beauty_v20241224',
    model_id: 'ctla885dhnqujk53vum0'
  },
  {
    model_name: 'beauty_v20241224_2',
    model_id: 'ctla9ngf27fsccbjvhm0'
  },
  {model_name: 'beauty_v20241228',model_id : 'ctnq53ldhnqi0fse88t0'}
]

// 生图参数
const params = ref({
  height: 1024,
  width: 1024,
  image_strength: 0.5,
  steps: 30,
  cfg_scale: 8.0,
  samples: 4,
  seed: 0,
  force_translation: false,
  format: 'JPG',
  watermark_config: { type: "MakaLogo" }
})

// 获取token
async function getToken() {
  token.value = generateToken()
}

// 获取模型列表
async function fetchModels() {
  try {
    const response = await fetch('/mhapi/v1/imgenstd/models?size=100&offset=0&mtp=ALL', {
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json()
    if (data.data && Array.isArray(data.data)) {
      models.value = data.data
      taskStatus.value = '获取模型列表成功'
      console.log('Models:', models.value)
    } else {
      throw new Error('Invalid models data')
    }
  } catch (error) {
    console.error('获取模型列表失败:', error)
    taskStatus.value = '获取模型列表失败: ' + error.message
  }
}

// 获取CN类型列表
async function fetchCNTypes() {
  // 清空之前的选择
  controlNetTypes.value = []
  selectedCNs.value = []

  if (!selectedModel.value) return
  
  taskStatus.value = '正在获取CN类型列表...'
  try {
    const response = await fetch(`/mhapi/v1/imgenstd/model_cn/${selectedModel.value}`, {
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json()
    if (data.data && Array.isArray(data.data)) {
      controlNetTypes.value = data.data
      taskStatus.value = '获取CN类型列表成功'
      console.log('CN Types:', controlNetTypes.value)
    } else {
      throw new Error('当前模型没有可用CN类型')
    }
  } catch (error) {
    console.error('获取CN类型列表失败:', error)
    taskStatus.value = '获取CN类型列表失败: ' + error.message
    controlNetTypes.value = []
  }
}

// 添加URL输入处理函数
async function handleUrlInput() {
  if (!imageUrl.value) return
  
  try {
    imagePreview.value = imageUrl.value
  } catch (error) {
    console.error('URL预览失败:', error)
    taskStatus.value = 'URL预览失败: ' + error.message
  }
}

// 生成图片
async function generateImage() {
  if (!prompt.value || !selectedModel.value) {
    alert('请填写提示词并选择模型')
    return
  }

  loading.value = true
  taskStatus.value = '正在生成...'
  generatedImages.value = []

  try {
    const requestBody = {
      model_id: selectedModel.value,
      prompt: prompt.value,
      ...params.value
    }

    if (imageUrl.value) {
      requestBody.img_url = imageUrl.value
    }

    if (selectedCNs.value.length > 0) {
      requestBody.controlnet_configs = selectedCNs.value.map(cn => ({
        type: cn,
        img_url: referenceUrls.value[cn] || ''
      })).filter(config => config.img_url)
    }

    if (loraModel.value) {
      requestBody.lora_configs = [{
        model_id: loraModel.value
      }]
    }

    // 提交生图任务
    const response = await fetch('/mhapi/v1/imgenstd/imgen', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })

    const data = await response.json()
    if (data.task_id) {
      pollTaskResult(data.task_id)
    } else {
      throw new Error('任务创建失败:' + data.error.details[1])
    }
  } catch (error) {
    console.error('生成图片失败:', error)
    taskStatus.value = '生成失败: ' + error.message
    loading.value = false
  }
}

// 轮询任务结果
async function pollTaskResult(taskId) {
  const checkResult = async () => {
    try {
      const response = await fetch(`/mhapi/v1/imgenstd/result/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token.value}`,
          'Content-Type': 'application/json'
        }
      })
      const data = await response.json()

      if (data.state === 'SUCCESS') {
        generatedImages.value = data.images
        taskStatus.value = '生成成功'
        loading.value = false
        emit('next-step')
      } else if (data.state === 'FAILED') {
        taskStatus.value = '生成失败: ' + data.state_message
        loading.value = false
      } else {
        // 继续轮询
        setTimeout(checkResult, 2000)
      }
    } catch (error) {
      console.error('查询任务状态失败:', error)
      taskStatus.value = '查询任务状态失败'
      loading.value = false
    }
  }

  checkResult()
}

// 组件加载时获取token和模型列表
onMounted(async () => {
  getToken()
  await fetchModels()
})

// 处理 CN 类型选择变化
function handleCNChange() {
  if (selectedCNs.value.length > 3) {
    selectedCNs.value = selectedCNs.value.slice(0, 3)
  }
}

// 处理参考图片 URL 输入
function handleReferenceUrlInput(cn) {
  if (!referenceUrls.value[cn]) return
  
  try {
    console.log(`Updated reference URL for ${cn}:`, referenceUrls.value[cn])
  } catch (error) {
    console.error('URL处理失败:', error)
    taskStatus.value = 'URL处理失败: ' + error.message
  }
}

async function loadSourceImages() {
  try {
    loading.value = true
    taskStatus.value = '正在加载素材库...'
    
    const urls = await fetchUrlSources(sourcePrefix.value)
    sourceUrls.value = urls
    taskStatus.value = `已加载 ${urls.length} 张素材图片`
  } catch (error) {
    console.error('加载素材库失败:', error)
    taskStatus.value = '加载素材库失败: ' + error.message
  } finally {
    loading.value = false
  }
}

function selectSourceImage(url) {
  imageUrl.value = url
  handleUrlInput()
}

async function loadCNSourceImages(cn) {
  try {
    loading.value = true
    taskStatus.value = '正在加载素材库...'
    
    const urls = await fetchUrlSources(cnSourcePrefix.value)
    cnSourceUrls.value = urls
    taskStatus.value = `已加载 ${urls.length} 张素材图片`
  } catch (error) {
    console.error('加载素材库失败:', error)
    taskStatus.value = '加载素材库失败: ' + error.message
  } finally {
    loading.value = false
  }
}

function selectCNSourceImage(url, cn) {
  referenceUrls.value[cn] = url
  handleReferenceUrlInput(cn)
}

function openImageBed() {
  window.open('https://www.ejiaimg.cn/upload', '_blank')
}

const urlInputContainerStyle = {
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  width: '100%'
}

const urlInputStyle = {
  flex: 1
}
</script>

<template>
  <div class="video-gen-container">
    <div class="video-gen">
      <div class="section">
        <h2>AI 图像生成</h2>
        
        <div class="control-panel">
          <div class="input-group">
            <label>选择模型（必选）:</label>
            <select v-model="selectedModel" @change="fetchCNTypes">
              <option value="">请选择模型</option>
              <option v-for="model in models" :key="model.id" :value="model.id">
                {{ model.name }} ({{ model.model_type }})
              </option>
            </select>
            <span class="model-desc" v-if="selectedModel && models.find(m => m.id === selectedModel)">
              {{ models.find(m => m.id === selectedModel).description }}
            </span>
          </div>
  
          <div class="input-group">
            <label>提示词（必选）:</label>
            <textarea v-model="prompt" placeholder="请输入提示词，支持中英文"></textarea>
          </div>
  
          <div class="input-group">
            <label>图生图（可选）:</label>
            <div class="url-input-container">
              <div class="input-wrapper">
                <input 
                  type="text"
                  v-model="imageUrl"
                  placeholder="请输入参考图片URL"
                  @change="handleUrlInput"
                  class="url-input"
                >
                <button 
                  @click="openImageBed"
                  class="image-bed-button"
                  title="打开图床"
                >
                  <span>图床</span>
                </button>
              </div>
              <div class="source-controls">
                <input 
                  type="text" 
                  v-model="sourcePrefix"
                  placeholder="输入素材库前缀"
                  class="prefix-input"
                >
                <button 
                  @click="loadSourceImages"
                  :disabled="loading"
                  class="source-button"
                >
                  加载素材
                </button>
              </div>
            </div>
            
            <!-- 素材预览网格 -->
            <div v-if="sourceUrls.length" class="source-grid">
              <div 
                v-for="(url, index) in sourceUrls" 
                :key="index" 
                class="source-item"
                @click="selectSourceImage(url)"
              >
                <img :src="url" :alt="`素材 ${index + 1}`">
              </div>
            </div>

            <div class="preview" v-if="imagePreview">
              <img :src="imagePreview" class="preview-image" alt="预览图">
            </div>
          </div>
  
          <div class="input-group">
            <label>选择CN类型（可选，最多三个）:</label>
            <select 
              v-model="selectedCNs" 
              multiple 
              @change="handleCNChange"
            >
              <option 
                v-for="cn in controlNetTypes" 
                :key="cn.cn" 
                :value="cn.cn"
              >
                {{ cn.name }} - {{ cn.description }}
              </option>
            </select>
            <span class="helper-text">已选择 {{ selectedCNs.length }}/3 个</span>
          </div>
  
          <div class="input-group">
            <label>我的 LORA 模型（可选）:</label>
            <select v-model="loraModel">
              <option value="">请选择 LORA 模型</option>
              <option 
                v-for="lora in loraOptions" 
                :key="lora.model_id" 
                :value="lora.model_id"
              >
                {{ lora.model_name }}
              </option>
            </select>
          </div>
  
          <div 
            v-for="cn in selectedCNs" 
            :key="cn" 
            class="input-group reference-image"
          >
            <label>{{ controlNetTypes.find(t => t.cn === cn)?.name }} 参考图片:</label>
            <div class="url-input-container">
              <div class="input-wrapper">
                <input 
                  type="text"
                  v-model="referenceUrls[cn]"
                  :placeholder="`请输入 ${controlNetTypes.find(t => t.cn === cn)?.name} 的参考图片URL`"
                  @change="handleReferenceUrlInput(cn)"
                  class="url-input"
                >
                <button 
                  @click="openImageBed"
                  class="image-bed-button"
                  title="打开图床"
                >
                  <span>图床</span>
                </button>
              </div>
              <div class="source-controls">
                <input 
                  type="text" 
                  v-model="cnSourcePrefix"
                  placeholder="输入素材库前缀"
                  class="prefix-input"
                >
                <button 
                  @click="loadCNSourceImages(cn)"
                  :disabled="loading"
                  class="source-button"
                >
                  加载素材
                </button>
              </div>
            </div>
            
            <!-- CN 参考图片素材预览网格 -->
            <div v-if="cnSourceUrls.length" class="source-grid">
              <div 
                v-for="(url, index) in cnSourceUrls" 
                :key="index" 
                class="source-item"
                @click="selectCNSourceImage(url, cn)"
              >
                <img :src="url" :alt="`素材 ${index + 1}`">
              </div>
            </div>

            <div class="preview" v-if="referenceUrls[cn]">
              <img :src="referenceUrls[cn]" class="preview-image" :alt="`${cn} 预览图`">
            </div>
          </div>
  
          <button @click="generateImage" :disabled="loading">
            {{ loading ? '生成中...' : '生成图片' }}
          </button>
        </div>
  
        <pre class="status-log" v-if="taskStatus">{{ taskStatus }}</pre>
  
        <div class="generated-images" v-if="generatedImages.length">
          <div v-for="image in generatedImages" :key="image.index" class="image-item">
            <img :src="image.raw" :alt="`生成图片 ${image.index + 1}`">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.video-gen-container {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  box-sizing: border-box;
  border-radius: 16px;
}

.video-gen {
  margin: 0 auto;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 15px 50px rgba(0,0,0,0.2);
  backdrop-filter: blur(5px);
  animation: fadeIn 0.5s ease-out;
}

.section {
  margin-bottom: 30px;
  padding: 25px;
  background: var(--section-bg);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
  margin-top: 30px;
}

.control-panel {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-bottom: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

label {
  font-weight: 500;
  color: var(--text-color);
}

select, textarea {
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
}

select {
  cursor: pointer;
}

select:hover {
  border-color: var(--primary-color);
}

select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

.url-input-container {
  width: 100%;
  margin-bottom: 10px;
}

.url-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
}

.url-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

.preview {
  margin-top: 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 10px;
  background: var(--input-bg);
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
}

button {
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  align-self: flex-start;
}

button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px var(--focus-shadow);
}

.status-log {
  margin: 15px 0;
  padding: 15px;
  background: var(--log-bg);
  color: var(--log-text);
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
  font-size: 0.9em;
  line-height: 1.5;
  border: 1px solid var(--border-color);
}

.model-desc {
  font-size: 0.9em;
  color: var(--subtitle-color);
  margin-top: 4px;
  font-style: italic;
}

.generated-images {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 20px;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.image-item img {
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 4px var(--card-shadow);
  transition: transform 0.3s ease;
}

.image-item img:hover {
  transform: scale(1.02);
}

select[multiple] {
  height: auto;
  min-height: 120px;
}

.helper-text {
  font-size: 0.9em;
  color: var(--subtitle-color);
  margin-top: 4px;
}

.reference-image {
  margin-top: 15px;
  padding: 15px;
  background: var(--section-bg);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.reference-image label {
  color: var(--text-color);
  font-weight: 500;
}

.source-controls {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.prefix-input {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  background: var(--input-bg);
  color: var(--text-color);
}

.source-button {
  white-space: nowrap;
  padding: 8px 16px;
}

.source-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 15px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  background: var(--section-bg);
  border-radius: 8px;
}

.source-item {
  aspect-ratio: 1;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.source-item:hover {
  border-color: var(--primary-color);
  transform: scale(1.05);
}

.source-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reference-image .source-controls {
  margin-top: 10px;
  margin-bottom: 15px;
}

.reference-image .source-grid {
  margin-bottom: 15px;
}

.reference-image {
  background: var(--card-bg);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.reference-image .preview {
  margin-top: 20px;
}

.input-wrapper {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.url-input {
  flex: 1;
}

.image-bed-button {
  padding: 8px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.image-bed-button:hover {
  background: var(--primary-hover);
  transform: translateY(-2px);
}

.url-input-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
</style> 